export interface MeasurementBoundaryPoint {
  x: number;
  y: number;
}

export interface MeasurementBoundaryBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

const MIN_BOUNDARY_POINTS = 3;
const MIN_BOX_CORNERS_INSIDE = 3;
const BOUNDARY_EPSILON = 1e-6;

const pointLiesOnSegment = (
  point: MeasurementBoundaryPoint,
  start: MeasurementBoundaryPoint,
  end: MeasurementBoundaryPoint
): boolean => {
  const squaredLength = (end.x - start.x) ** 2 + (end.y - start.y) ** 2;
  if (squaredLength <= BOUNDARY_EPSILON ** 2) {
    return (point.x - start.x) ** 2 + (point.y - start.y) ** 2 <= BOUNDARY_EPSILON ** 2;
  }

  const length = Math.sqrt(squaredLength);
  const cross = (point.y - start.y) * (end.x - start.x) - (point.x - start.x) * (end.y - start.y);
  if (Math.abs(cross) / length > BOUNDARY_EPSILON) return false;

  const dot = (point.x - start.x) * (end.x - start.x) + (point.y - start.y) * (end.y - start.y);
  if (dot < -BOUNDARY_EPSILON * length) return false;

  return dot - squaredLength <= BOUNDARY_EPSILON * length;
};

const rayCastContainsPoint = (boundary: MeasurementBoundaryPoint[], point: MeasurementBoundaryPoint): boolean => {
  let inside = false;
  for (let i = 0, j = boundary.length - 1; i < boundary.length; j = i++) {
    const current = boundary[i];
    const previous = boundary[j];
    if (
      current.y > point.y !== previous.y > point.y &&
      point.x < ((previous.x - current.x) * (point.y - current.y)) / (previous.y - current.y) + current.x
    ) {
      inside = !inside;
    }
  }
  return inside;
};

/**
 * UIBezierPath-compatible point-in-polygon contract shared by deferred analysis
 * and web editing. DAB uses `UIBezierPath.contains` for stand-count keypoints and
 * its three-corner box rule. Edge inclusion matches the observed Core Graphics
 * behavior behind DAB's `UIBezierPath.contains` calls.
 * A missing/malformed boundary means "no spatial restriction" here, so every
 * point is treated as inside. Callers whose domain means "select nothing" without
 * a boundary (for example a customer rerun script) must guard it. The boundary
 * and tested point must use the same coordinate frame; the edge tolerance is
 * expressed in that frame's pixel units and is independent of edge length.
 */
export const isPointInsideMeasurementBoundary = (
  boundary: MeasurementBoundaryPoint[],
  point: MeasurementBoundaryPoint
): boolean => {
  if (boundary.length < MIN_BOUNDARY_POINTS) return true;

  for (let i = 0, j = boundary.length - 1; i < boundary.length; j = i++) {
    if (pointLiesOnSegment(point, boundary[j], boundary[i])) return true;
  }
  return rayCastContainsPoint(boundary, point);
};

/**
 * iOS leaf-area measurements use a distinct polygon contract from box/keypoint
 * containment: an exact polygon vertex is included, then the ordinary ray-cast
 * decides every other edge point. Keep this separate so tightening annotation
 * containment cannot silently change LAI pixels.
 */
export const isPointInsideAreaMeasurementBoundary = (
  boundary: MeasurementBoundaryPoint[],
  point: MeasurementBoundaryPoint
): boolean => {
  if (boundary.length < MIN_BOUNDARY_POINTS) return true;
  if (boundary.some((vertex) => point.x === vertex.x && point.y === vertex.y)) return true;
  return rayCastContainsPoint(boundary, point);
};

/**
 * Matches the capture-editor call sites, which explicitly require three contained
 * corners. A missing/malformed boundary cannot exclude a box, so it is not outside.
 */
export const isBoxOutsideMeasurementBoundary = (
  bbox: MeasurementBoundaryBox,
  boundary: MeasurementBoundaryPoint[]
): boolean => {
  if (boundary.length < MIN_BOUNDARY_POINTS) return false;
  const corners = [
    { x: bbox.x, y: bbox.y },
    { x: bbox.x + bbox.width, y: bbox.y },
    { x: bbox.x + bbox.width, y: bbox.y + bbox.height },
    { x: bbox.x, y: bbox.y + bbox.height },
  ];
  return corners.filter((corner) => isPointInsideMeasurementBoundary(boundary, corner)).length < MIN_BOX_CORNERS_INSIDE;
};
